/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Box, Divider, Stack, styled } from "@mui/material";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button } from "../../../components/Button";
import { Loader, LoadingBox } from "../../../components/Loader";
import MaterialSymbol from "../../../components/MaterialSymbol";
import apiClient from "../../../services/api-client";
import { useAppDispatch } from "../../../store/rootStore";
import { error } from "../../../util/log";
import { useSeedBranch } from "../../../util/useBranch";
import { selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import { ContactComponents, useContactInfo } from "../../drawdown/v1/forms/ContactInfo";
import SeedFormTitle from "../components/SeedFormTitle";
import { SeedOrderSummaryTotal } from "../components/SeedOrderSummaryTotal";
import { SeedOrderTable } from "../components/SeedOrderTable";
import { SEED_SUCCESS_ROUTE } from "../constants";
import SeedCartSlice, { selectSeedOrder, selectSeedOrderSyncing } from "../storage/seedCart.slice";
import { getSeedCart } from "../storage/seedCartThunk";
import { SeedOrderCheckout } from "../types/SeedOrderFormType";
import { useCheckLocalStorage } from "../utils/checkSeedInLocalStorage";
import { getAllProductIds } from "../utils/getAllProductIds";

const ButtonBox = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: "24px 16px",
  marginLeft: "auto",
  width: "100%",
  flexDirection: "row-reverse",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column-reverse",
    paddingBottom: "8px",
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  width: "auto",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

export const SeedOrderSummaryForm = () => {
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const isTfo = currentAccount?.hasTfoAccess || false;
  const accountNumber = currentAccount?.accountNumber ?? "";
  const navigate = useNavigate();

  const seedOrder = useSelector(selectSeedOrder(accountNumber));
  const dispatch = useAppDispatch();

  const { email, phone, name, reference, validateContactInfo, ContactInfoComponent } =
    useContactInfo({
      components: [
        ContactComponents.NAME,
        ContactComponents.EMAIL,
        ContactComponents.PHONE,
        ContactComponents.REFERENCE,
      ],
      isTfo,
    });

  const submitOrder = () => {
    const accountNumber = seedOrder?.accountNumber;
    if (validateContactInfo() && accountNumber) {
      setLoading(true);
      apiClient
        .post<void, SeedOrderCheckout>(`/seed-orders/${seedOrder?.id}/checkout`, {
          accountNumber,
          orderVersion: seedOrder?.version ?? "",
          customerName: name ?? "",
          email: email ?? "",
          phone: phone ?? "",
          customerReference: reference ?? "",
        })
        .then((result) => {
          if (result.status === 200) {
            dispatch(SeedCartSlice.actions.emptySeedOrder({ accountNumber }));
            navigate(SEED_SUCCESS_ROUTE);
          }
          setLoading(false);
        })
        .catch((err: AxiosError) => {
          setLoading(false);
          error(err.message);
        });
    }
  };

  const [loading, setLoading] = useState(false);

  const loader = React.useMemo(
    () => (
      <LoadingBox pt={3}>
        <Loader type="inline-flex" title="Loading..." />
      </LoadingBox>
    ),
    []
  );

  if (loading) {
    return loader;
  }
  if (!seedOrder) {
    return null;
  }

  return (
    <>
      <Box bgcolor={colors.shade0} data-html2canvas-ignore={true}>
        <SeedFormTitle title="Your details" />
        <Box p={2}>{ContactInfoComponent}</Box>
        <Divider />
      </Box>
      <ButtonBox gap={2}>
        <SubmitButton variant="contained" onClick={submitOrder}>
          Submit order
        </SubmitButton>
        <Button
          variant="outlined"
          startIcon={
            <MaterialSymbol sx={{ transform: "rotate(-180deg)", fontWeight: "normal" }}>
              arrow_right_alt
            </MaterialSymbol>
          }
          onClick={() => {
            navigate("/seed");
          }}
        >
          Edit Order
        </Button>
      </ButtonBox>
    </>
  );
};

export const SeedOrderSummaryDetails = () => {
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const accountNumber = currentAccount?.accountNumber ?? "";

  const syncing = useSelector(selectSeedOrderSyncing);

  const seedOrder = useSelector(selectSeedOrder(accountNumber));
  const dispatch = useAppDispatch();

  const { checkSeedInLocalStorage } = useCheckLocalStorage();
  const branchId = useSeedBranch();

  useEffect(() => {
    if (seedOrder?.mixes) {
      const productIds = getAllProductIds(seedOrder.mixes);
      checkSeedInLocalStorage(productIds, accountNumber ?? "", branchId);
    }
  }, [seedOrder]);

  useEffect(() => {
    if (accountNumber) {
      dispatch(getSeedCart({ accountNumber }));
    }
  }, [accountNumber]);

  if (!seedOrder) {
    return null;
  }

  return (
    <Stack direction="column" gap={4} pt={4} pb={4} className={syncing ? "shimmer-container" : ""}>
      <SeedOrderTable seedOrder={seedOrder} />
      <SeedOrderSummaryTotal seedOrder={seedOrder} />
    </Stack>
  );
};
